<template>
  <div class="attachments">
    <popup
      :title="$t('view.attachments.dialog.delete.title')"
      ref="attachmentDelDialog"
      class="attachments__delete-dialog"
    >
      <p>
        {{ $t('view.attachments.dialog.delete.text1') }}
        <b>{{ (attachmentToDelete) ? attachmentToDelete.name : '' }}</b>
        {{ $t('view.attachments.dialog.delete.text2') }}
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.attachmentDelDialog.close()">{{ $t('btn.cancel') }}</btn>
        <btn icon="trash" @click="deleteAttachment()" color="red">{{ $t('btn.delete') }}</btn>
      </template>
    </popup>

    <load-screen :startingText="$t('view.attachments.uploading')" ref="loader"></load-screen>
    <div class="attachments__editor editor">
      <div class="attachments__top-buttons">
        <btn icon="sync" @click="sync(null)">{{ $t('view.attachments.syncAll') }}</btn>
      </div>
      <div class="attachments__grid">
        <div
          v-for="(attachment, k, index) in $store.state.attachments"
          :key="attachment.id"
          ref="cards"
          class="attachments__card"
        >
          <div class="attachments__card-preview">
            <div
              v-if="attachmentToEdit.id !== attachment.id"
              class="attachments__edit-card-preview"
            >
              <img
                v-if="['.jpg', '.jpeg', '.png', '.gif'].indexOf(attachment.extension) !== -1"
                class="attachments__card-preview-image"
                alt="Attachment preview"
                :src="attachment.url"
              >
              <div v-else class="attachments__create-preview-placeholder">
                <fai icon="video" size="4x"></fai>
                <div class="attachments__create-text">{{ $t('view.attachments.video') }}</div>
              </div>
            </div>
            <div class="attachments__edit-card-preview" v-else>
              <img
                v-if="['.jpg', '.jpeg', '.png', '.gif'].indexOf(attachmentToEdit.extension) !== -1"
                class="attachments__card-preview-image"
                alt="Attachment preview"
                :src="(attachmentToEdit.fileUrl) ? attachmentToEdit.fileUrl : attachment.url"
              >
              <div v-else class="attachments__create-preview-placeholder">
                <fai icon="video" size="4x"></fai>
                <div class="attachments__create-text">{{ $t('view.attachments.video') }}</div>
              </div>
            </div>
            <input
              v-if="attachmentToEdit.id === attachment.id"
              type="file"
              class="attachments__file-input"
              ref="fileEdit"
              accept="image/jpeg, image/png, image/gif, video/mp4"
              @change="fileInput(true)"
            >
          </div>
          <div class="attachments__card-footer">
            <div class="attachments__card-info">
              <div v-if="attachmentToEdit.id !== attachment.id">
                <div class="attachments__card-title">{{ attachment.name }}</div>
                <div class="attachments__card-info-row">
                  {{ $t('view.attachments.status') }}:
                  <span
                    v-show="attachment.attachment_id && !attachment.force_update"
                    class="attachments__card-info-value attachments__card-info-value--green"
                  >{{ $t('view.attachments.inSync') }}</span>
                  <span
                    v-show="attachment.attachment_id && attachment.force_update"
                    class="attachments__card-info-value attachments__card-info-value--orange"
                  >{{ $t('view.attachments.needUpdate') }}</span>
                  <span
                    v-show="!attachment.attachment_id"
                    class="attachments__card-info-value attachments__card-info-value--red"
                  >{{ $t('view.attachments.new') }}</span>
                </div>
                <div class="attachments__card-info-row">
                  {{ $t('view.attachments.size') }}:
                  <span
                    class="attachments__card-info-value attachments__card-info-value"
                  >{{ convertBytes(attachment.file_size) }}</span>
                </div>
              </div>
              <div v-else>
                <in
                  v-model="attachmentToEdit.name"
                  :label="$t('view.attachments.name')"
                  class="attachments__name-input"
                  @enter="saveChanges(index)"
                ></in>
              </div>
            </div>
            <div class="attachments__card-buttons">
              <div
                @click="sync(attachment.id)"
                class="attachments__card-button"
                :class="(attachment.attachment_id && !attachment.force_update) ? 'attachments__card-button--inactive' : ''"
              >{{ $t('btn.sync') }}</div>
              <div
                @click="setAttachmentToEdit(attachment, index)"
                v-show="attachmentToEdit.id !== attachment.id"
                class="attachments__card-button"
              >{{ $t('btn.edit') }}</div>
              <div
                @click="saveChanges(index)"
                v-show="attachmentToEdit.id === attachment.id"
                class="attachments__card-button attachments__card-button--green"
              >{{ $t('btn.save') }}</div>
              <div
                v-if="attachmentToEdit.id !== attachment.id"
                class="attachments__card-button attachments__card-button--red"
                @click="attachmentToDelete = attachment; $refs.attachmentDelDialog.open()"
              >{{ $t('btn.delete') }}</div>
              <div
                class="attachments__card-button attachments__card-button--red"
                @click="unsetAttachmentToEdit(index)"
                v-else
              >{{ $t('btn.cancel') }}</div>
            </div>
          </div>
        </div>
        <div class="attachments__create attachments__card">
          <div class="attachments__card-preview attachments__card-preview--creator">
            <input
              type="file"
              class="attachments__file-input"
              ref="file"
              accept="image/jpeg, image/png, image/gif, video/mp4"
              @change="fileInput(false)"
            >
            <div
              v-if="!newAttachment.file"
              class="attachments__create-preview-empty attachments__create-preview-placeholder"
            >
              <fai icon="plus" size="4x"></fai>
              <div class="attachments__create-text">{{ $t('view.attachments.pick') }}</div>
            </div>
            <div class="attachments__edit-card-preview" v-else>
              <img
                class="attachments__card-preview-image"
                :src="newAttachment.fileUrl"
                alt="Image to upload"
                v-if="newAttachment.file && ['image/jpeg', 'image/png', 'image/gif'].indexOf(newAttachment.file.type) !== -1"
              >
              <div v-else class="attachments__create-preview-placeholder">
                <fai icon="video" size="4x"></fai>
                <div class="attachments__create-text">{{ $t('view.attachments.video') }}</div>
              </div>
            </div>
          </div>
          <div class="attachments__card-footer">
            <in
              v-model="newAttachment.name"
              :label="$t('view.attachments.name')"
              class="attachments__name-input"
              @enter="createAttachment"
            ></in>
            <div class="attachments__card-buttons">
              <div
                @click="createAttachment"
                class="attachments__card-button attachments__card-button--fullwidth"
              >{{ $t('btn.create') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cleanAxios from 'axios'
import axios from '../axios'

export default {
  data () {
    return {
      reversedCardRef: null,
      newAttachment: {
        name: '',
        file: null,
        fileUrl: ''
      },
      attachmentToDelete: null,
      attachmentToEdit: {
        id: null,
        name: '',
        file: null,
        fileUrl: '',
        extension: ''
      }
    }
  },

  methods: {
    convertBytes (bytes) {
      if (bytes === 0) return '0 Bytes'

      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB']

      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async uploadToS3 (file) {
      try {
        const signedUrlReq = await axios.get(`/api/attachments/signed-url?type=${file.type}`)
        const options = {
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'public-read'
          }
        }
        const uploadReq = await axios.put(signedUrlReq.data.url, file, options)
        return uploadReq.request.responseURL.replace(/\?.*$/, '')
      } catch (e) {
        throw e
      }
    },

    async fileInput (edit) {
      if (edit) {
        this.attachmentToEdit.file = this.$refs.fileEdit[0].files[0]
        this.attachmentToEdit.fileUrl = URL.createObjectURL(this.attachmentToEdit.file)
        this.attachmentToEdit.extension = '.' + this.attachmentToEdit.file.type.replace(/image\/|video\//, '')
      } else {
        this.newAttachment.file = this.$refs.file.files[0]
        this.newAttachment.fileUrl = URL.createObjectURL(this.newAttachment.file)
      }
    },

    async createAttachment () {
      try {
        this.$refs.loader.show(6000)
        const imageUrl = await this.uploadToS3(this.newAttachment.file)
        const createReq = await axios.post('/api/attachments/attachment', {
          name: this.newAttachment.name,
          url: imageUrl,
          file_size: this.newAttachment.file.size
        })
        this.$store.commit('ADD_ATTACHMENT', createReq.data.created)
        this.newAttachment.name = ''
        this.newAttachment.fileUrl = ''
        this.newAttachment.file = null
        this.$refs.loader.hide()
        this.notify('success', 'view.attachments.success.create')
      } catch (e) {
        this.$refs.loader.hide()
        this.notify('error', 'view.attachments.error.create')
        console.error(e)
      }
    },

    async sync (id) {
      try {
        let arr = []
        if (id) arr.push(id)
        else {
          for (let uuid in this.$store.state.attachments) {
            if (!this.$store.state.attachments[uuid].force_update) continue
            arr.push(this.$store.state.attachments[uuid].id)
          }
        }
        this.$refs.loader.show(arr.length * 7000)
        const syncReq = await axios.post('/api/attachments/sync', {
          attachments: arr
        })
        this.$store.commit('ATTACHMENTS_SYNC', syncReq.data.updated)
        this.$refs.loader.hide()
        this.notify('success', 'view.attachments.success.sync')
      } catch (error) {
        this.$refs.loader.hide()
        this.notify('error', 'view.attachments.error.sync')
        console.error(e)
      }
    },

    async deleteAttachment () {
      try {
        await axios.delete('/api/attachments/attachment', {
          data: {
            id: this.attachmentToDelete.id
          }
        })

        this.$store.commit('DELETE_ATTACHMENT', this.attachmentToDelete.uuid)
        this.$refs.attachmentDelDialog.close()
        this.notify('success', 'view.attachments.success.delete')
      } catch (e) {
        this.$refs.attachmentDelDialog.close()
        this.notify('error', 'view.attachments.error.delete')
        console.error(e)
      }
    },

    async setAttachmentToEdit (attachment, index) {
      this.$refs.cards[index].classList.add('attachments__card--spinned')
      await this.delayMs(200)
      if (this.reversedCardRef) this.reversedCardRef.classList.add('attachments__card--spinned')
      await this.delayMs(100)
      this.$refs.cards[index].classList.remove('attachments__card--spinned')
      this.attachmentToEdit.file = null
      this.attachmentToEdit.fileUrl = ''
      this.attachmentToEdit.id = attachment.id
      this.attachmentToEdit.name = attachment.name
      this.attachmentToEdit.extension = attachment.extension
      await this.delayMs(200)
      if (this.reversedCardRef) this.reversedCardRef.classList.remove('attachments__card--spinned')
      this.reversedCardRef = this.$refs.cards[index]
    },

    async unsetAttachmentToEdit (index) {
      this.$refs.cards[index].classList.add('attachments__card--spinned')
      await this.delayMs(300)
      this.attachmentToEdit.id = null
      this.$refs.cards[index].classList.remove('attachments__card--spinned')
      this.reversedCardRef = null
    },

    async saveChanges (index) {
      try {
        let imageUrl = null
        if (this.attachmentToEdit.file) {
          this.$refs.loader.show(5000)
          imageUrl = await this.uploadToS3(this.attachmentToEdit.file)
        }
        const updateReq = await axios.put('/api/attachments/attachment', {
          id: this.attachmentToEdit.id,
          name: this.attachmentToEdit.name,
          url: imageUrl,
          file_size: (this.attachmentToEdit.file) ? this.attachmentToEdit.file.size : null
        })
        this.$store.commit('ATTACHMENT_UPDATE', updateReq.data.updated)
        this.unsetAttachmentToEdit(index)
        this.$refs.loader.hide()
        this.notify('success', 'view.attachments.success.changes')
      } catch (e) {
        this.$refs.loader.hide()
        this.notify('error', 'view.attachments.error.changes')
        console.error(e)
      }
    }
  }
}
</script>

<style lang="scss">
.attachments {
  &__top-buttons {
    @include alignedFlex(flex-end);
    padding: 15px 20px;
  }

  &__delete-dialog {
    & > .popup__window {
      max-width: 35% !important;
    }
  }

  &__grid {
    display: grid;
    padding-top: 8px;
    padding-bottom: 24px;
    grid-row-gap: 10px;
    grid-column-gap: 10px;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }

  &__card {
    @include boxShadow(0px, 0px, 10px, -9px, rgba(0, 0, 0, 0.75));
    height: 400px;
    max-width: 330px;
    background-color: white;
    border-radius: 3px;
    overflow: hidden;
    transition: transform 0.2s ease;
    will-change: transform;

    &--spinned {
      transform: rotateY(90deg);
    }
  }

  &__card-preview {
    @include alignedFlex();
    position: relative;
    height: 63%;
    background-color: $bgLightGrey;

    &--creator {
      cursor: pointer;
      &:hover {
        .attachments__create-preview-empty {
          transform: scale(1.1);
        }
      }
    }
  }

  &__card-preview-image {
    max-width: 100%;
    max-height: 100%;
  }

  &__edit-card-preview {
    @include alignedFlex();
    width: 100%;
    height: 100%;
  }

  &__card-footer {
    @include boxShadow(0px, -1px, 19px, -19px, rgba(0, 0, 0, 0.75));
    position: relative;
    height: calc(37% - 8px);
    padding: 4px 8px;
  }

  &__card-buttons {
    @include alignedFlex();
    position: absolute;
    bottom: 0;
    left: 0;
    height: 27%;
    width: 100%;
    border-top: 1px solid $borderGrey;
  }

  &__card-button {
    @include alignedFlex();
    height: 100%;
    width: 33.33%;
    font-weight: 700;
    cursor: pointer;
    background-color: white;
    transition: background-color 0.2s ease;
    will-change: background-color;

    &:first-child {
      border-right: 1px solid $borderGrey;
    }

    &:last-child {
      border-left: 1px solid $borderGrey;
    }

    &--red {
      color: $danger;
    }

    &--green {
      color: $successHover;
    }

    &--inactive {
      pointer-events: none;
      color: $inactiveGrey;
      background-color: white !important;
    }

    &--fullwidth {
      width: 100%;
      border: 0 !important;
    }

    &:hover {
      background-color: rgb(250, 250, 250);
    }
  }

  &__card-title {
    font-size: 1.6em;
  }

  &__card-info-row {
    font-style: italic;
  }

  &__card-info-value {
    font-style: normal;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.9em;
    letter-spacing: 0.025em;

    &--green {
      color: $successHover;
    }

    &--orange {
      color: $primary;
    }

    &--red {
      color: $danger;
    }
  }

  &__create-preview-empty {
    transition: transform 0.2s ease;
  }

  &__create-preview-placeholder {
    text-align: center;
    color: $inactiveGrey;
  }

  &__create-text {
    margin-top: 10px;
    font-size: 1.2em;
    text-transform: uppercase;
  }

  &__name-input {
    margin-top: 15px;
  }

  &__file-input {
    width: 100%;
    height: 100%;
    opacity: 0;
    position: absolute;
    background-color: pink;
    z-index: 10;
    cursor: pointer;
  }
}
</style>
